import { Box, Button, SxProps } from "@outschool/backpack";
import { MainToolbarPinnedTabs } from "@outschool/gql-backend-generated";
import {
  IconDefinition,
  faBullhorn,
  faCalendarAlt,
  faChalkboardTeacher,
  faChartLine,
  faCog,
  faComment,
  faFileAlt,
  faGift,
  faHeart,
  faHome,
  faMoneyBillAlt,
  faPiggyBank,
  faQuestionCircle,
  faSearch,
  faSignIn,
  faSignOut,
  faUserPlus,
  faUsersCog,
  fasGift
} from "@outschool/icons";
import {
  useCurrencyLocalization,
  useTranslation
} from "@outschool/localization";
import {
  browseRootPath,
  giftCardLandingPath,
  isHomePage,
  leadActivitiesPath,
  searchPath,
  sellerOrgPath,
  teachPath,
  teachTipsUrl,
  teacherSchedulePath
} from "@outschool/routes";
import {
  AuthTrigger,
  useLoginFlowContext,
  useSession
} from "@outschool/ui-auth";
import { ExternalLink } from "@outschool/ui-components-shared";
import { NavHelpLink } from "@outschool/ui-components-website";
import { Icon } from "@outschool/ui-legacy-component-library";
import { Screen } from "@outschool/ui-utils";
import { SystemStyleObject } from "@styled-system/css";
import { union } from "lodash";
import React from "react";
import { useLocation } from "react-router-dom";

import { FeatureFlag } from "../../../shared/Env";
import * as Routes from "../../../shared/Routes";
import * as User from "../../../shared/User";
import { usePricingOfferAdmin } from "../../hooks/CurrentUser";
import { useCurrentUserLearners } from "../../hooks/Learners";
import {
  getOrgAdminRoutesFromUser,
  isBuyerOrgAdmin
} from "../../lib/BuyerOrgAdmins";
import { useFeatureFlag } from "../../lib/FeatureFlags";
import { UserMode, useUserMode } from "../../lib/UserMode";
import { isOrganizationAdminWhoCanEnroll } from "../../lib/Users";
import Link from "../Link";
import LocalizationPickerHeaderMenuItem from "../localization/LocalizationPickerHeaderMenuItem";
import UnreadMessagesCount from "../UnreadMessagesCount";
import LearnerModeLinks from "./LearnerModeLinks";
import { AVAILABLE_PROFILE_TABS } from "./TeachToolbar";
import WalletFundsNavItem from "./WalletFundsNavItem";

interface HeaderMenuProps {
  onClose?: any;
}

export default function HeaderMenu(props: HeaderMenuProps) {
  const { onClose } = props;
  const { isLoggedIn, currentUser, isLeader } = useSession();
  const learners = useCurrentUserLearners();
  const isDesktop = Screen.useIsLarge();
  const location = useLocation();
  const isHomePath = isHomePage(location.pathname);
  const pricingOfferAdminOrgUid = usePricingOfferAdmin();
  const { hasLoaded: localizationHasLoaded, currencyCode } =
    useCurrencyLocalization();
  const userMode = useUserMode();
  const isEducatorMode = userMode === UserMode.Educator;
  const buyerOrgAdminModeEnabled = useFeatureFlag(
    FeatureFlag.BuyerOrgAdminMode
  );
  const hasLearner = learners.length > 0;
  const isBuyerOrgAdminMode =
    buyerOrgAdminModeEnabled && isBuyerOrgAdmin(currentUser);

  const displayLocalizationPicker =
    localizationHasLoaded && (isLoggedIn || !isDesktop) && currencyCode;

  const localizationPicker = displayLocalizationPicker && (
    <>
      <LocalizationPickerHeaderMenuItem />
      <HeaderMenuSeparator />
    </>
  );

  const showWalletFunds =
    isLoggedIn && !!currentUser?.pricingOffer && !isDesktop;
  const { t } = useTranslation("client\\components\\nav\\HeaderMenu");

  const menuProps = {
    currentUser,
    pricingOfferAdminOrgUid,
    onClose,
    localizationPicker,
    isHomePath,
    isMobile: !isDesktop,
    isLeader,
    showWalletFunds,
    hasLearner,
    t
  };
  let menuItems;
  if (!isLoggedIn) {
    menuItems = <LoggedOutMenu {...menuProps} t={t} />;
  } else if (isEducatorMode) {
    menuItems = (
      <LoggedInMenu onClose={onClose} t={t}>
        <EducatorMenu {...menuProps} t={t} />
      </LoggedInMenu>
    );
  } else if (buyerOrgAdminModeEnabled && isBuyerOrgAdminMode) {
    menuItems = (
      <LoggedInMenu onClose={onClose} t={t}>
        <BuyerOrgAdminMenu {...menuProps} t={t} />
      </LoggedInMenu>
    );
  } else {
    menuItems = (
      <LoggedInMenu onClose={onClose} t={t}>
        <ParentMenu {...menuProps} t={t} />
      </LoggedInMenu>
    );
  }

  return (
    <Box
      flex
      sx={{
        flexDirection: "column",
        paddingY: "1em",
        overflowY: "auto",
        overscrollBehaviorY: !isDesktop ? "none" : "inherit"
      }}
      data-test-id="header-menu"
    >
      {menuItems}
    </Box>
  );
}

function LoggedInMenu({ children, onClose, t }) {
  return (
    <>
      {children}

      <HeaderMenuSeparator />

      <Link to={Routes.parentSettingsPath()} onClick={onClose}>
        <HeaderMenuItem label={t("Profile & Settings")} icon={faCog} />
      </Link>

      <HelpLink onClick={onClose} t={t} />

      <Link to={Routes.logoutPath()} onClick={onClose}>
        <HeaderMenuItem label={t("Log out")} icon={faSignOut} />
      </Link>
    </>
  );
}

function MobileEducatorItems({ onClose, currentUser, t }) {
  const PlainItemLink = ({
    to,
    label,
    icon = undefined,
    trackingName = undefined,
    children = undefined
  }) => (
    <Link to={to} onClick={onClose} trackingName={trackingName}>
      <HeaderMenuItem label={label} icon={icon}>
        {children}
      </HeaderMenuItem>
    </Link>
  );

  const EducatorItemLink = props => {
    const { badge, ...rest } = props;
    const { currentUser } = useSession();

    if (!!badge) {
      return (
        <PlainItemLink {...rest}>
          {!!badge && (
            <UnreadMessagesCount
              color="red"
              count={User.unreadConversationsCount(currentUser)}
            />
          )}
        </PlainItemLink>
      );
    }

    return <PlainItemLink {...rest} />;
  };

  const menuTabs = union(
    currentUser?.leader?.mainToolbarPinnedTabs,
    Array.from(AVAILABLE_PROFILE_TABS.keys())
  ).filter(tab => Object.values(MainToolbarPinnedTabs).includes(tab));

  const conversations = (
    <PlainItemLink
      to={Routes.conversationsPath(UserMode.Educator)}
      label={t("Conversations")}
    >
      {/* @ts-ignore TS(2322): Type 'Element' is not assignable to type 'undefine... Remove this comment to see the full error message */}
      <UnreadMessagesCount
        color="red"
        count={User.unreadConversationsCount(currentUser)}
      />
    </PlainItemLink>
  );
  const schedule = (
    <PlainItemLink to={teacherSchedulePath()} label={t("Schedule")} />
  );
  const library = (
    <ExternalLink url={teachTipsUrl()}>
      <HeaderMenuItem label={t("Educator Library")} />
    </ExternalLink>
  );

  return currentUser?.sellerOrg?.currentUserIsTeacher ? (
    <>
      <PlainItemLink to={Routes.teachBasePath()} label={t("Home")} />
      {conversations}
      {schedule}
      <PlainItemLink to={Routes.teacherSectionsPath()} label={t("Sections")} />
      {library}
      <HeaderMenuSeparator />
    </>
  ) : (
    <>
      <PlainItemLink to={Routes.teachBasePath()} label={t("Home")} />
      {User.hasTeacherApplication(currentUser) && (
        <PlainItemLink to={Routes.teacherApplyPath()} label={t("Apply")} />
      )}
      {menuTabs.map(tab => {
        const tabInfo = AVAILABLE_PROFILE_TABS.get(tab);

        return (
          // @ts-ignore TS(2532): Object is possibly 'undefined'.
          <EducatorItemLink key={`tab-item-${tabInfo.label}`} {...tabInfo} />
        );
      })}
      {library}
      <HeaderMenuSeparator />
    </>
  );
}

function BuyerOrgAdminMenu({
  currentUser,
  localizationPicker,
  onClose,
  isMobile,
  pricingOfferAdminOrgUid,
  t
}) {
  const isEnrollingOrgAdmin = isOrganizationAdminWhoCanEnroll(currentUser);
  const { homeRoute, analyticsRoute } = getOrgAdminRoutesFromUser(currentUser);

  return (
    <>
      {isMobile && (
        <HomeLinkPath
          onClick={onClose}
          homePath={homeRoute ?? browseRootPath()}
          t={t}
        />
      )}
      {isMobile && isEnrollingOrgAdmin && (
        <FindClassesLink onClick={onClose} t={t} />
      )}
      {isMobile && pricingOfferAdminOrgUid && analyticsRoute && (
        <Link to={analyticsRoute} onClick={onClose}>
          <HeaderMenuItem label={t("Analytics")} icon={faChartLine} />
        </Link>
      )}
      {localizationPicker}
      <ConversationsLink
        to={Routes.conversationsPath(UserMode.Parent)}
        onClick={onClose}
        currentUser={currentUser}
        t={t}
      />
      <FavoritesLink onClick={onClose} t={t} />
      {isEnrollingOrgAdmin && <TransactionsLink onClick={onClose} t={t} />}
    </>
  );
}

function EducatorMenu({
  pricingOfferAdminOrgUid,
  currentUser,
  onClose,
  isMobile,
  hasLearner,
  t
}) {
  return (
    <>
      {isMobile && (
        <>
          <ParentHomeLink onClick={onClose} t={t} />
          <HeaderMenuSeparator />
        </>
      )}
      {hasLearner && (
        <>
          <LearnerModeLinks onClick={onClose} />
          <HeaderMenuSeparator />
        </>
      )}
      {isMobile && (
        <MobileEducatorItems
          onClose={onClose}
          currentUser={currentUser}
          t={t}
        />
      )}

      {currentUser?.latestSellerOrgApplication && (
        <SellerOrgManageLink onClick={onClose} t={t} />
      )}
      {isMobile && pricingOfferAdminOrgUid && (
        <Link
          to={Routes.pricingOfferAdminDashboardPath(
            // @ts-ignore TS(2345): Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
            null,
            pricingOfferAdminOrgUid
          )}
          onClick={onClose}
          trackingName="nav_pricing_offer_dashbaord"
        >
          <HeaderMenuItem label={t("Dashboard")} icon={faChartLine} />
        </Link>
      )}

      <Link to={Routes.referTeachersPath()} onClick={onClose}>
        <HeaderMenuItem label={t("Invite a Teacher")} icon={faBullhorn} />
      </Link>
      <Link to={Routes.invitePath()} onClick={onClose}>
        <HeaderMenuItem label={t("Invite a Parent")} icon={faBullhorn} />
      </Link>
    </>
  );
}

function ParentMenu({
  onClose,
  isMobile,
  currentUser,
  pricingOfferAdminOrgUid,
  localizationPicker,
  isLeader,
  showWalletFunds,
  hasLearner,
  t
}) {
  const learnerEnrollmentRequests = [];
  const isOrgApplicant = currentUser?.latestSellerOrgApplication;
  const isOrgAdmin = currentUser?.sellerOrg?.currentUserIsOwner;
  const isMobileEducator = isMobile && isLeader && !isOrgAdmin;
  const referMenuLabel = t("Give $20, Get $20");

  return (
    <>
      {isMobileEducator && (
        <>
          <EducatorHomeLink onClick={onClose} t={t} />
          <HeaderMenuSeparator />
        </>
      )}
      {showWalletFunds && (
        <>
          <HeaderMenuItem>
            <WalletFundsNavItem onClick={onClose} isMobile />
          </HeaderMenuItem>
          <HeaderMenuSeparator />
        </>
      )}
      {hasLearner && (
        <>
          <LearnerModeLinks onClick={onClose} />
          <HeaderMenuSeparator />
        </>
      )}
      <Link to={Routes.invitePath()} trackingName="nav_g20g20_link_dropdown">
        <HeaderMenuItem
          icon={fasGift}
          iconSx={{ color: "peacock700" }}
          label={referMenuLabel}
          sx={{ color: "peacock700", fontWeight: "600" }}
        />
      </Link>
      <HeaderMenuSeparator />
      {isMobile && <HomeLink onClick={onClose} t={t} />}
      {isMobile && <FindClassesLink onClick={onClose} t={t} />}
      <ConversationsLink
        to={Routes.conversationsPath(
          isLeader ? UserMode.Educator : UserMode.Parent
        )}
        onClick={onClose}
        currentUser={currentUser}
        t={t}
      />
      <FavoritesLink onClick={onClose} t={t} />
      <HeaderMenuSeparator />
      {localizationPicker}
      <Link
        to={Routes.learnSchedulePath()}
        onClick={onClose}
        trackingName="nav_account_schedule"
      >
        <HeaderMenuItem label={t("Learner Schedule")} icon={faCalendarAlt} />
      </Link>
      <Link
        to={Routes.learnerEnrollmentRequestsPath()}
        onClick={onClose}
        trackingName="nav_learner_requests"
      >
        <HeaderMenuItem
          label={t("Learner Allowance")}
          icon={faPiggyBank}
          badgeNumber={learnerEnrollmentRequests?.length}
        />
      </Link>
      <TransactionsLink onClick={onClose} t={t} />
      <Link
        to={Routes.transcriptsPath()}
        onClick={onClose}
        trackingName="nav_account_transcripts"
      >
        <HeaderMenuItem label={t("Transcripts")} icon={faFileAlt} />
      </Link>

      {(isOrgApplicant || !isLeader || pricingOfferAdminOrgUid) && (
        <HeaderMenuSeparator />
      )}

      {isOrgApplicant && <SellerOrgManageLink onClick={onClose} t={t} />}
      {!isMobileEducator && <TeachLink onClick={onClose} t={t} />}
      {isMobile && pricingOfferAdminOrgUid && (
        <Link
          to={Routes.pricingOfferAdminDashboardPath(
            // @ts-ignore TS(2345): Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
            null,
            pricingOfferAdminOrgUid
          )}
          onClick={onClose}
          trackingName="nav_pricing_offer_dashbaord"
        >
          <HeaderMenuItem label={t("Dashboard")} icon={faChartLine} />
        </Link>
      )}

      <HeaderMenuSeparator />

      <Link to={Routes.referTeachersPath()} onClick={onClose}>
        <HeaderMenuItem label={t("Invite a Teacher")} icon={faBullhorn} />
      </Link>

      <Link to={giftCardLandingPath()} onClick={onClose}>
        <HeaderMenuItem label={t("Give a Gift Card")} icon={faGift} />
      </Link>
    </>
  );
}

function LoggedOutMenu({ onClose, isMobile, localizationPicker, t }) {
  const { enterLoginFlow } = useLoginFlowContext();
  return (
    <>
      {isMobile && <HomeLink onClick={onClose} t={t} />}
      {isMobile && <FindClassesLink onClick={onClose} t={t} />}
      <Link to={giftCardLandingPath()} onClick={onClose}>
        <HeaderMenuItem label={t("Give a Gift Card")} icon={faGift} />
      </Link>
      <HelpLink onClick={onClose} t={t} />
      <HeaderMenuSeparator />
      {localizationPicker}
      <Button
        variant="link"
        sx={{
          fontWeight: "normal",
          justifyContent: "start"
        }}
        onClick={() => {
          enterLoginFlow({
            authTrigger: AuthTrigger.JOIN_OUTSCHOOL
          });
          onClose();
        }}
      >
        <HeaderMenuItem
          label={t("Join")}
          icon={faUserPlus}
          sx={{ width: "100%" }}
        />
      </Button>
      <Link to={Routes.loginPath()} onClick={onClose}>
        <HeaderMenuItem label={t("Log In")} icon={faSignIn} />
      </Link>
    </>
  );
}

function HomeLinkPath({ onClick, homePath, t }) {
  return (
    <Link to={homePath} onClick={onClick}>
      <HeaderMenuItem label={t("Home")} icon={faHome} />
    </Link>
  );
}

function HomeLink({ onClick, t }) {
  return <HomeLinkPath onClick={onClick} homePath={browseRootPath()} t={t} />;
}

function ParentHomeLink({ onClick, t }) {
  return (
    <Button
      component={Link}
      to={Routes.parentRootPath()}
      onClick={onClick}
      sx={{
        marginX: "0.5em"
      }}
    >
      {t`Switch to Parent`}
    </Button>
  );
}

function EducatorHomeLink({ onClick, t }) {
  return (
    <Button
      component={Link}
      to={teachPath()}
      onClick={onClick}
      sx={{
        marginX: "0.5em"
      }}
    >
      {t`Switch to Educator`}
    </Button>
  );
}

function FindClassesLink({ onClick, t }) {
  return (
    <Link onClick={onClick} to={searchPath()} trackingName="nav_find_classes">
      <HeaderMenuItem label={t("Find Classes")} icon={faSearch} />
    </Link>
  );
}

function SellerOrgManageLink({ onClick, t }) {
  return (
    <Link onClick={onClick} to={sellerOrgPath()} trackingName="nav_manage_link">
      <HeaderMenuItem label={t("Manage")} icon={faUsersCog} />
    </Link>
  );
}

function TeachLink({ onClick, t }) {
  const { isLeader } = useSession();
  return (
    <Link
      onClick={onClick}
      to={isLeader ? teachPath() : leadActivitiesPath()}
      trackingName="nav_teach_link"
    >
      <HeaderMenuItem label={t("Teach")} icon={faChalkboardTeacher} />
    </Link>
  );
}

function HelpLink({ onClick, t }) {
  return (
    <NavHelpLink onClick={onClick}>
      <HeaderMenuItem label={t("Help")} icon={faQuestionCircle} />
    </NavHelpLink>
  );
}

function ConversationsLink({ to, onClick, currentUser, t }) {
  return (
    <Link to={to} onClick={onClick} trackingName="nav_account_conversations">
      <HeaderMenuItem label={t("Conversations")} icon={faComment}>
        <UnreadMessagesCount
          color="red"
          count={User.unreadConversationsCount(currentUser)}
        />
      </HeaderMenuItem>
    </Link>
  );
}

function FavoritesLink({ onClick, t }) {
  return (
    <Link
      to={Routes.savedClassesPath()}
      onClick={onClick}
      trackingName="nav_account_favorites"
    >
      <HeaderMenuItem label={t("Favorites")} icon={faHeart} />
    </Link>
  );
}

function TransactionsLink({ onClick, t }) {
  return (
    <Link
      to={Routes.transactionsPath()}
      onClick={onClick}
      trackingName="nav_account_payments"
    >
      <HeaderMenuItem label={t("Transactions")} icon={faMoneyBillAlt} />
    </Link>
  );
}

export function HeaderMenuSeparator() {
  return (
    <Box
      sx={{
        height: "1px",
        marginY: "1em",
        backgroundColor: "#ecf1f8",
        flexShrink: 0
      }}
    />
  );
}

export function HeaderMenuItem({
  label,
  icon,
  children = null,
  iconSx = {},
  // @ts-ignore TS(2322): Type 'null' is not assignable to type 'number'.
  badgeNumber = null,
  sx = {}
}: {
  label?: string;
  icon?: IconDefinition;
  children?: React.ReactNode;
  iconSx?: SystemStyleObject;
  sx?: SxProps;
  badgeNumber?: number;
}) {
  return (
    <Box
      flex
      sx={[
        {
          paddingY: "0.5em",
          paddingX: "1em",
          color: "grey.900",
          alignItems: "center",

          "&:hover": {
            backgroundColor: "#ecf1f8"
          },

          position: "relative"
        },
        ...(Array.isArray(sx) ? sx : [sx])
      ]}
    >
      {icon && (
        <Icon
          fixedWidth
          icon={icon}
          sx={{ marginRight: "small", color: "link", ...iconSx }}
        />
      )}
      {!!badgeNumber && (
        <Box
          sx={{
            position: "absolute",
            left: 30,
            top: "8px",
            background: "red",
            color: "common.white",
            textAlign: "center",
            borderRadius: "50%",
            fontSize: ".85rem",
            width: "1.25rem",
            height: "1.25rem",
            lineHeight: "1.25rem",
            fontWeight: "fontWeightBold"
          }}
        >
          {badgeNumber}
        </Box>
      )}
      {label}
      {children}
    </Box>
  );
}
